<template>
  <div class="task-page">
    <div class="task-header">
      <div class="task-header-actions">
        <button type="button" class="ghost-btn" @click="goBack">
          <i class="bi bi-arrow-left"></i>
          返回
        </button>
        <button type="button" class="ghost-btn" :disabled="loading" @click="loadDetail">
          <i class="bi bi-arrow-clockwise" :class="{ spinning: loading }"></i>
          刷新
        </button>
      </div>

      <div class="task-header-main">
        <div class="task-title-row">
          <div class="task-filename" :title="detail.sourceFileName">{{ detail.sourceFileName || '文档处理' }}</div>
          <span v-if="detail.serviceTypeLabel" class="task-badge mode">{{ detail.serviceTypeLabel }}</span>
          <span v-if="detail.platform" class="task-badge platform">{{ detail.platform }}</span>
        </div>
        <div class="task-stats">
          <div class="stat-item">
            <span class="stat-label">消耗点数:</span>
            <span class="stat-value">{{ consumedDisplay }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">处理进度:</span>
            <div class="progress-wrap">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
              </div>
              <span class="progress-text">{{ progressPercent }}%</span>
            </div>
          </div>
        </div>
      </div>

      <div class="task-header-right">
        <button
            v-if="!readonly"
            type="button"
            class="regenerate-btn"
            :disabled="regenerating || !taskId || !canRegenerateDocument"
            :title="canRegenerateDocument ? '按当前段落结果重新合并文档' : '文档处理完成后可用'"
            @click="handleRegenerate">
          <i class="bi bi-file-earmark-arrow-up" :class="{ spinning: regenerating }"></i>
          {{ regenerating ? '生成中...' : '重新生成文档' }}
        </button>
        <button
            type="button"
            class="download-btn"
            :class="{ disabled: !canDownload }"
            :disabled="!canDownload"
            @click="handleDownload">
          <i class="bi bi-download"></i>
          下载结果
        </button>
      </div>
    </div>

    <div class="task-body">
      <div v-if="loadError" class="task-error">{{ loadError }}</div>
      <div v-else-if="loading && !detail.taskNo" class="task-loading">加载中...</div>

      <div v-else class="paragraph-list">
        <div v-if="detail.needPayment" class="payment-panel">
          <div class="payment-left">
            <div v-if="packagesLoading" class="payment-loading">正在匹配推荐套餐...</div>
            <template v-else-if="recommendedProduct">
              <div class="recommend-tag">推荐套餐</div>
              <div class="payment-plan-name">{{ recommendedProduct.name }}</div>
              <div class="payment-price">{{ paymentPrice }}<span class="unit">元</span></div>
              <div class="payment-quota">到账 {{ formatQuota(recommendedProduct.quotaAmount) }} 点</div>
              <div class="payment-rate">{{ paymentRateLabel }}</div>
              <div class="payment-need-hint">
                还需 <strong>{{ formatQuota(remainingNeed) }}</strong> 点
                <span v-if="surplusQuotaAfterPay > 0">，付后余 {{ formatQuota(surplusQuotaAfterPay) }} 点</span>
              </div>
              <div class="payment-qrcode">
                <div v-if="paymentLoading" class="qrcode-status">正在生成二维码...</div>
                <div v-else-if="payError" class="qrcode-status error">{{ payError }}</div>
                <img
                  v-else-if="qrImageUrl"
                  :src="qrImageUrl"
                  alt="微信支付二维码"
                  class="qrcode-img"
                  width="106"
                  height="106">
                <div v-else class="qrcode-status">点击下方按钮获取二维码</div>
              </div>
              <p class="payment-pay-hint">微信扫码支付</p>
              <button
                v-if="!qrImageUrl && wechatEnabled && !paymentLoading"
                type="button"
                class="resume-btn"
                @click="initPayment">
                立即支付
              </button>
              <button
                v-if="payError && wechatEnabled"
                type="button"
                class="plan-btn"
                :disabled="paymentLoading"
                @click="initPayment">
                重新获取二维码
              </button>
            </template>
            <div v-else class="payment-loading">暂无可用套餐</div>
            <button type="button" class="plan-btn" @click="openRecharge">选购更多套餐</button>
            <button type="button" class="resume-btn secondary" :disabled="resuming" @click="handleResume">
              {{ resuming ? '恢复中...' : '我已补充点数' }}
            </button>
            <p class="payment-tip">虚拟商品一经支付不支持退款</p>
          </div>
          <div class="payment-right">
            <h2>点数已用尽，</h2>
            <p>
              支付后继续处理剩余的<span class="highlight">{{ detail.remainingChars || 0 }}</span>字符
              <span v-if="showRemainingQuotaDetail">（约需 {{ formatQuota(remainingNeed) }} 点）</span>
            </p>
            <ul class="payment-features">
              <li v-for="item in paymentFeatures" :key="item.title">
                <i :class="item.icon"></i>
                <div>
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.desc }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <template v-for="(section, sectionIndex) in displaySections">
          <div
              v-if="section.type === 'table'"
              :key="'table-' + section.tableIndex + '-' + sectionIndex"
              class="table-card">
            <div class="table-index">{{ section.startSeqNo + 1 }}</div>
            <div class="table-card-header">
              <span class="table-badge">表格 {{ (section.tableIndex != null ? section.tableIndex : 0) + 1 }}</span>
            </div>
            <div class="table-scroll">
              <table class="doc-table">
                <tbody>
                  <tr v-for="(row, rowIdx) in section.rows" :key="'row-' + rowIdx">
                    <td v-for="(cell, colIdx) in row" :key="'col-' + colIdx">
                      <template v-if="cell">
                        <div class="table-cell-seq">#{{ cell.seqNo + 1 }}</div>
                        <template v-if="isParagraphDiffMode(cell.seqNo)">
                          <div class="table-cell-label">修改对比</div>
                          <TextDiffView
                              class="table-cell-diff"
                              :original="cell.sourceText"
                              :modified="cell.outputText" />
                        </template>
                        <template v-else>
                          <div class="table-cell-label">原文</div>
                          <div class="table-cell-text">{{ cell.sourceText }}</div>
                          <template v-if="cell.outputText && !cell.skipped">
                            <div class="table-cell-label output">结果</div>
                            <div class="table-cell-text output">{{ cell.outputText }}</div>
                          </template>
                        </template>
                        <div class="table-cell-status">
                          <div v-if="cell.status === '2' && cell.skipped" class="paragraph-skip-tip">
                            <i class="bi bi-dash-circle"></i>无需改写
                          </div>
                          <div v-else-if="isParagraphWaiting(cell)" class="paragraph-pay-tip">
                            <i class="bi bi-exclamation-circle"></i>待充值
                          </div>
                          <div v-else-if="cell.status === '2'" class="paragraph-done-tip">
                            <i class="bi bi-check-circle"></i>已完成
                          </div>
                          <div v-else-if="cell.status === '1'" class="paragraph-processing-tip">
                            <i class="bi bi-hourglass-split"></i>处理中
                          </div>
                          <div v-else class="paragraph-pending-tip">
                            <i class="bi bi-circle"></i>等待
                          </div>
                        </div>
                        <div v-if="canShowParagraphDiff(cell) || canReviseParagraph(cell)" class="table-cell-actions">
                          <button
                              v-if="canShowParagraphDiff(cell)"
                              type="button"
                              class="paragraph-diff-toggle"
                              @click="toggleParagraphDiff(cell.seqNo)">
                            <i class="bi bi-arrow-left-right"></i>
                            {{ paragraphDiffToggleLabel(cell.seqNo) }}
                          </button>
                          <button
                              v-if="canReviseParagraph(cell)"
                              type="button"
                              class="paragraph-action-btn ghost"
                              :disabled="isParagraphActionLoading(cell.seqNo)"
                              @click="handleUseSourceParagraph(cell)">
                            使用原文
                          </button>
                          <button
                              v-if="canReviseParagraph(cell)"
                              type="button"
                              class="paragraph-action-btn primary"
                              :disabled="isParagraphActionLoading(cell.seqNo) || (cell.regenerateRemaining != null && cell.regenerateRemaining <= 0)"
                              @click="handleRegenerateParagraph(cell)">
                            {{ regenerateParagraphLabel(cell) }}
                          </button>
                        </div>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
              v-else
              :key="'para-' + section.item.seqNo"
              class="paragraph-card">
          <div class="paragraph-index">{{ section.item.seqNo + 1 }}</div>
          <template v-if="isParagraphDiffMode(section.item.seqNo)">
            <div class="paragraph-row paragraph-diff-row">
              <div class="paragraph-label">对比</div>
              <TextDiffView
                  class="paragraph-diff-view"
                  :original="section.item.sourceText"
                  :modified="section.item.outputText" />
            </div>
          </template>
          <template v-else>
          <div class="paragraph-row">
            <div class="paragraph-label">原文</div>
            <div class="paragraph-text">{{ section.item.sourceText }}</div>
          </div>
            <div v-if="section.item.outputText && !section.item.skipped" class="paragraph-row output-row">
            <div class="paragraph-label">结果</div>
            <div class="paragraph-text">{{ section.item.outputText }}</div>
          </div>
          </template>
          <div class="paragraph-footer">
            <div class="paragraph-footer-status">
              <div v-if="section.item.status === '2' && section.item.skipped" class="paragraph-skip-tip">
                <i class="bi bi-dash-circle"></i>
                无需改写，已保留原文
              </div>
              <div v-else-if="isParagraphWaiting(section.item)" class="paragraph-pay-tip">
                <i class="bi bi-exclamation-circle"></i>
                点数不足，充值后继续处理
              </div>
              <div v-else-if="section.item.status === '2'" class="paragraph-done-tip">
                <i class="bi bi-check-circle"></i>
                已完成
              </div>
              <div v-else-if="section.item.status === '1'" class="paragraph-processing-tip">
                <i class="bi bi-hourglass-split"></i>
                处理中...
              </div>
              <div v-else class="paragraph-pending-tip">
                <i class="bi bi-circle"></i>
                等待处理
              </div>
            </div>
            <div class="paragraph-footer-actions">
              <button
                  v-if="canShowParagraphDiff(section.item)"
                  type="button"
                  class="paragraph-diff-toggle"
                  @click="toggleParagraphDiff(section.item.seqNo)">
                <i class="bi bi-arrow-left-right"></i>
                {{ paragraphDiffToggleLabel(section.item.seqNo) }}
              </button>
              <template v-if="canReviseParagraph(section.item)">
              <button
                  type="button"
                  class="paragraph-action-btn ghost"
                  :disabled="isParagraphActionLoading(section.item.seqNo)"
                  @click="handleUseSourceParagraph(section.item)">
                <i
                    v-if="isParagraphActionLoading(section.item.seqNo, 'use-source')"
                    class="bi bi-arrow-repeat spinning"></i>
                {{ isParagraphActionLoading(section.item.seqNo, 'use-source') ? '恢复中...' : '使用原文' }}
              </button>
              <button
                  type="button"
                  class="paragraph-action-btn primary"
                  :disabled="isParagraphActionLoading(section.item.seqNo) || (section.item.regenerateRemaining != null && section.item.regenerateRemaining <= 0)"
                  @click="handleRegenerateParagraph(section.item)">
                <i
                    v-if="isParagraphActionLoading(section.item.seqNo, 'regenerate')"
                    class="bi bi-arrow-repeat spinning"></i>
                {{ regenerateParagraphLabel(section.item) }}
              </button>
              </template>
            </div>
          </div>
        </div>
        </template>
      </div>
    </div>

    <button
        v-if="detail.needPayment"
        type="button"
        class="payment-affix"
        @click="scrollToPayment">
      点数不足，点击前往购买点数
    </button>

  </div>
</template>

<script>
import {
  getProcessTaskDetail,
  getProcessTaskProgress,
  getProcessParagraphOutputs,
  regenerateProcessOutput,
  regenerateProcessParagraph,
  useSourceProcessParagraph,
  resumeProcessTask
} from '@/api/portal/process'
import { getProductList } from '@/api/portal/product'
import { createOrder } from '@/api/portal/order'
import { getPaymentConfig, createPay, getOrderStatus } from '@/api/portal/pay'
import { pickProductForQuota, resolveRemainingNeed } from '@/utils/recommendProduct'
import TextDiffView from '@/components/workbench/TextDiffView.vue'

const ACTIVE_STATUSES = ['0', '1', '4']
const TASK_POLL_INTERVAL_MS = 8000
const PAY_CHANNEL_WECHAT = 'WECHAT_NATIVE'
const PAY_POLL_INTERVAL_MS = 2000
const PAY_POLL_MAX_TIMES = 150

function buildTableRows(items) {
  const rowMap = new Map()
  let maxCol = 0
  for (const item of items) {
    const rowIndex = item.rowIndex != null ? item.rowIndex : 0
    const colIndex = item.colIndex != null ? item.colIndex : 0
    if (!rowMap.has(rowIndex)) {
      rowMap.set(rowIndex, new Map())
    }
    rowMap.get(rowIndex).set(colIndex, item)
    maxCol = Math.max(maxCol, colIndex)
  }
  return Array.from(rowMap.keys())
    .sort((a, b) => a - b)
    .map(rowIndex => {
      const colMap = rowMap.get(rowIndex)
      const cells = []
      for (let colIndex = 0; colIndex <= maxCol; colIndex++) {
        cells.push(colMap.get(colIndex) || null)
      }
      return cells
    })
}

function buildDisplaySections(paragraphs) {
  if (!paragraphs || !paragraphs.length) {
    return []
  }
  const sections = []
  let index = 0
  while (index < paragraphs.length) {
    const item = paragraphs[index]
    if (!item.inTable) {
      sections.push({ type: 'paragraph', item })
      index++
      continue
    }
    const tableIndex = item.tableIndex
    const tableItems = []
    while (index < paragraphs.length
        && paragraphs[index].inTable
        && paragraphs[index].tableIndex === tableIndex) {
      tableItems.push(paragraphs[index])
      index++
    }
    sections.push({
      type: 'table',
      tableIndex,
      rows: buildTableRows(tableItems),
      startSeqNo: tableItems[0] ? tableItems[0].seqNo : 0
    })
  }
  return sections
}

export default {
  name: 'ProcessTaskView',
  components: {
    TextDiffView
  },
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    balance: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      taskId: '',
      detail: {
        paragraphs: []
      },
      loading: false,
      regenerating: false,
      paragraphActionSeqNo: null,
      paragraphActionType: '',
      paragraphDiffMode: {},
      resuming: false,
      loadError: '',
      pollTimer: null,
      paymentLoading: false,
      payError: '',
      codeUrl: '',
      currentOrderNo: '',
      currentPayChannel: PAY_CHANNEL_WECHAT,
      payPollTimer: null,
      payPollCount: 0,
      packagesLoading: false,
      wechatEnabled: false,
      paymentPackages: [],
      paymentFeatures: [
        { icon: 'bi bi-shield-check', title: '知网AI率 <20%', desc: '深度改写，文本更自然真实' },
        { icon: 'bi bi-arrow-repeat', title: '7天内不限次数免费改写', desc: '行业保障，无限修改到满意为止' },
        { icon: 'bi bi-credit-card', title: '品质兜底承诺', desc: '全程专业专项AI，逐字打磨贴合学术规范' },
        { icon: 'bi bi-lock', title: '不收录不公开', desc: '文档加密处理，保护科研隐私安全' }
      ]
    }
  },
  computed: {
    progressPercent() {
      return this.detail.progress != null ? this.detail.progress : 0
    },
    consumedDisplay() {
      if (this.detail.consumedQuota != null && this.detail.consumedQuota > 0) {
        return this.detail.consumedQuota
      }
      return this.detail.quotaCost || 0
    },
    remainingNeed() {
      return resolveRemainingNeed(this.detail)
    },
    recommendedProduct() {
      return pickProductForQuota(this.remainingNeed, this.paymentPackages)
    },
    showRemainingQuotaDetail() {
      const chars = this.detail.remainingChars || 0
      return this.remainingNeed > 0 && this.remainingNeed !== chars
    },
    surplusQuotaAfterPay() {
      if (!this.recommendedProduct || !this.recommendedProduct.quotaAmount) {
        return 0
      }
      return Math.max(this.recommendedProduct.quotaAmount - this.remainingNeed, 0)
    },
    paymentPrice() {
      if (!this.recommendedProduct) {
        return '—'
      }
      return this.formatPrice(this.recommendedProduct.price)
    },
    paymentRateLabel() {
      if (this.recommendedProduct && this.recommendedProduct.perThousand) {
        return this.recommendedProduct.perThousand
      }
      if (!this.recommendedProduct || !this.recommendedProduct.quotaAmount) {
        return '充值后继续处理剩余字符'
      }
      const perThousand = (Number(this.recommendedProduct.price) / this.recommendedProduct.quotaAmount) * 1000
      return `约${perThousand.toFixed(1)}元/千字符`
    },
    qrImageUrl() {
      if (!this.codeUrl) {
        return ''
      }
      return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(this.codeUrl)}`
    },
    canDownload() {
      return !!this.detail.outputFileUrl
    },
    canRegenerateDocument() {
      return this.detail.status === '2' && this.detail.submitType === 'F'
    },
    displaySections() {
      return buildDisplaySections(this.detail.paragraphs || [])
    }
  },
  watch: {
    '$route.query.taskId': {
      immediate: true,
      handler(val) {
        this.taskId = val || ''
        this.paragraphDiffMode = {}
        if (this.taskId && !this.readonly) {
          this.loadDetail()
        } else if (this.readonly) {
          this.loadError = '请先登录后查看任务详情'
        }
      }
    },
    readonly(val) {
      if (val) {
        this.stopPolling()
        this.stopPayPolling()
        this.loadError = '请先登录后查看任务详情'
      } else if (this.taskId) {
        this.loadDetail()
      }
    },
    'detail.needPayment'(val) {
      if (val) {
        this.preparePaymentPanel()
      } else {
        this.stopPayPolling()
        this.paymentPackages = []
        this.codeUrl = ''
        this.payError = ''
      }
    }
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopPayPolling()
  },
  methods: {
    loadDetail() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (!this.taskId) {
        this.loadError = '缺少任务 ID'
        return
      }
      this.loading = true
      getProcessTaskDetail(this.taskId, false)
        .then(res => {
          this.detail = res.data || { paragraphs: [] }
          this.loadError = ''
          this.syncPolling()
          if (this.detail.needPayment) {
            this.preparePaymentPanel()
          }
        })
        .catch(err => {
          this.loadError = err.message || '加载任务失败'
          this.stopPolling()
        })
        .finally(() => {
          this.loading = false
        })
    },
    syncPolling() {
      if (ACTIVE_STATUSES.includes(this.detail.status)) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    startPolling() {
      if (this.pollTimer) {
        return
      }
      this.pollTimer = setInterval(() => {
        if (!this.taskId || this.loading) {
          return
        }
        getProcessTaskProgress(this.taskId)
          .then(res => {
            const needOutputs = this.mergeTaskProgress(res.data || { paragraphs: [] })
            if (needOutputs.length) {
              this.fetchParagraphOutputs(needOutputs)
            }
            if (!ACTIVE_STATUSES.includes(this.detail.status)) {
              this.stopPolling()
              this.loadDetail()
            }
          })
          .catch(() => {})
      }, TASK_POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    handleResume() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      this.resuming = true
      resumeProcessTask(this.taskId)
        .then(() => {
          this.loadDetail()
        })
        .catch(err => {
          this.loadError = err.message || '恢复任务失败'
        })
        .finally(() => {
          this.resuming = false
        })
    },
    openRecharge() {
      this.$emit('require-recharge')
    },
    formatPrice(price) {
      const num = Number(price)
      if (Number.isNaN(num)) {
        return '0'
      }
      return num % 1 === 0 ? String(num) : num.toFixed(2).replace(/\.?0+$/, '')
    },
    formatQuota(value) {
      const num = Number(value)
      if (Number.isNaN(num)) {
        return '0'
      }
      return num.toLocaleString()
    },
    preparePaymentPanel() {
      this.loadPaymentPackages()
        .then(() => {
          if (this.recommendedProduct && this.wechatEnabled) {
            this.initPayment()
          }
        })
        .catch(err => {
          this.payError = err.message || '加载套餐失败'
        })
    },
    loadPaymentPackages() {
      if (this.packagesLoading) {
        return Promise.resolve()
      }
      this.packagesLoading = true
      this.payError = ''
      return Promise.all([getPaymentConfig(), getProductList()])
        .then(([configRes, productRes]) => {
          const cfg = configRes.data || {}
          this.wechatEnabled = !!cfg.wechatEnabled
          this.paymentPackages = productRes.data || []
          if (!this.paymentPackages.length) {
            throw new Error('暂无可用充值套餐')
          }
          if (!this.recommendedProduct) {
            throw new Error('未找到合适套餐，请点击选购更多套餐')
          }
        })
        .finally(() => {
          this.packagesLoading = false
        })
    },
    initPayment() {
      if (this.readonly || !this.detail.needPayment || this.paymentLoading) {
        return
      }
      if (!this.recommendedProduct) {
        this.payError = '请先等待推荐套餐加载完成'
        return
      }
      if (this.codeUrl && this.payPollTimer && !this.payError) {
        return
      }
      if (!this.wechatEnabled) {
        this.payError = '微信支付暂未开通，请点击选购更多套餐'
        return
      }
      this.paymentLoading = true
      this.payError = ''
      this.codeUrl = ''
      this.stopPayPolling()

      this.startWeChatPay()
        .catch(err => {
          this.payError = err.message || '发起支付失败，请稍后重试'
        })
        .finally(() => {
          this.paymentLoading = false
        })
    },
    startWeChatPay() {
      return createOrder({ productId: this.recommendedProduct.productId })
        .then(createRes => {
          const orderNo = createRes.data && createRes.data.orderNo
          if (!orderNo) {
            throw new Error('创建订单失败')
          }
          this.currentOrderNo = orderNo
          this.currentPayChannel = PAY_CHANNEL_WECHAT
          return createPay({ orderNo, channel: PAY_CHANNEL_WECHAT })
        })
        .then(payRes => {
          this.codeUrl = (payRes.data && payRes.data.codeUrl) || ''
          if (!this.codeUrl) {
            throw new Error('未获取到支付二维码')
          }
          this.startPayPolling()
        })
    },
    startPayPolling() {
      this.stopPayPolling()
      this.payPollCount = 0
      this.payPollTimer = setInterval(() => {
        this.payPollCount += 1
        if (this.payPollCount > PAY_POLL_MAX_TIMES) {
          this.stopPayPolling()
          this.payError = '支付超时，如已付款请点击「我已补充点数」'
          return
        }
        if (!this.currentOrderNo) {
          return
        }
        getOrderStatus(this.currentOrderNo, this.currentPayChannel)
          .then(res => {
            const data = res.data || {}
            if (data.paid) {
              this.stopPayPolling()
              this.onPaySuccess(data)
            }
          })
          .catch(() => {})
      }, PAY_POLL_INTERVAL_MS)
    },
    stopPayPolling() {
      if (this.payPollTimer) {
        clearInterval(this.payPollTimer)
        this.payPollTimer = null
      }
      this.payPollCount = 0
    },
    onPaySuccess(payData) {
      this.$emit('recharge-success', payData)
      this.payError = ''
      this.handleResume()
    },
    handleDownload() {
      if (this.canDownload) {
        window.open(this.detail.outputFileUrl, '_blank')
      }
    },
    handleRegenerate() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (!this.taskId || this.regenerating || !this.canRegenerateDocument) {
        return
      }
      this.regenerating = true
      this.loadError = ''
      regenerateProcessOutput(this.taskId)
        .then(res => {
          this.detail = res.data || { paragraphs: [] }
        })
        .catch(err => {
          this.loadError = err.message || '重新生成文档失败'
        })
        .finally(() => {
          this.regenerating = false
        })
    },
    canReviseParagraph(item) {
      if (this.readonly) {
        return false
      }
      return this.detail.status === '2'
        && this.detail.submitType === 'F'
        && item.status === '2'
        && !item.skipped
    },
    canShowParagraphDiff(item) {
      return !!(item
        && item.status === '2'
        && item.sourceText
        && item.outputText
        && !item.skipped)
    },
    isParagraphDiffMode(seqNo) {
      return !!this.paragraphDiffMode[seqNo]
    },
    paragraphDiffToggleLabel(seqNo) {
      return this.isParagraphDiffMode(seqNo) ? '查看改后文字' : '查看修改文字'
    },
    toggleParagraphDiff(seqNo) {
      this.$set(this.paragraphDiffMode, seqNo, !this.paragraphDiffMode[seqNo])
    },
    isParagraphActionLoading(seqNo, actionType) {
      if (this.paragraphActionSeqNo !== seqNo) {
        return false
      }
      if (!actionType) {
        return true
      }
      return this.paragraphActionType === actionType
    },
    regenerateParagraphLabel(item) {
      if (this.isParagraphActionLoading(item.seqNo, 'regenerate')) {
        return '生成中...'
      }
      const remaining = item.regenerateRemaining != null ? item.regenerateRemaining : 3
      if (remaining <= 0) {
        return '次数已用完'
      }
      return `重新生成（剩 ${remaining} 次）`
    },
    handleUseSourceParagraph(item) {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (!this.taskId || this.isParagraphActionLoading(item.seqNo)) {
        return
      }
      this.paragraphActionSeqNo = item.seqNo
      this.paragraphActionType = 'use-source'
      this.loadError = ''
      useSourceProcessParagraph(this.taskId, item.seqNo)
        .then(res => {
          this.detail = res.data || { paragraphs: [] }
        })
        .catch(err => {
          this.loadError = err.message || '恢复原文失败'
        })
        .finally(() => {
          this.paragraphActionSeqNo = null
          this.paragraphActionType = ''
        })
    },
    handleRegenerateParagraph(item) {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (!this.taskId || this.isParagraphActionLoading(item.seqNo)) {
        return
      }
      const remaining = item.regenerateRemaining != null ? item.regenerateRemaining : 3
      if (remaining <= 0) {
        return
      }
      this.paragraphActionSeqNo = item.seqNo
      this.paragraphActionType = 'regenerate'
      this.loadError = ''
      regenerateProcessParagraph(this.taskId, item.seqNo)
        .then(res => {
          this.detail = res.data || { paragraphs: [] }
          this.$emit('process-success')
        })
        .catch(err => {
          const message = err.message || '段落重新生成失败'
          this.loadError = message
          if (message.indexOf('点数不足') >= 0) {
            this.scrollToPayment()
          }
        })
        .finally(() => {
          this.paragraphActionSeqNo = null
          this.paragraphActionType = ''
        })
    },
    isParagraphWaiting(item) {
      return item.status === '4'
    },
    mergeTaskProgress(progress) {
      if (!progress || !progress.taskNo) {
        return []
      }
      const prevParagraphs = this.detail.paragraphs || []
      const prevBySeq = {}
      prevParagraphs.forEach(item => {
        prevBySeq[item.seqNo] = item
      })

      this.detail.status = progress.status
      this.detail.progress = progress.progress
      this.detail.consumedQuota = progress.consumedQuota
      this.detail.remainingChars = progress.remainingChars
      this.detail.remainingQuota = progress.remainingQuota
      this.detail.needPayment = progress.needPayment
      this.detail.errorMsg = progress.errorMsg
      this.detail.finishedAt = progress.finishedAt
      if (progress.outputFileUrl) {
        this.detail.outputFileUrl = progress.outputFileUrl
      }

      const needOutputs = []
      const merged = (progress.paragraphs || []).map(item => {
        const prev = prevBySeq[item.seqNo]
        const next = prev
          ? {
              ...prev,
              status: item.status,
              charCount: item.charCount,
              quotaCost: item.quotaCost,
              regenerateCount: item.regenerateCount,
              regenerateRemaining: item.regenerateRemaining
            }
          : { ...item }
        if (item.status === '2' && (!prev || prev.status !== '2' || !prev.outputText)) {
          needOutputs.push(item.seqNo)
        }
        return next
      })
      this.detail.paragraphs = merged.length ? merged : prevParagraphs
      return needOutputs
    },
    fetchParagraphOutputs(seqNos) {
      if (!seqNos.length || !this.taskId) {
        return Promise.resolve()
      }
      return getProcessParagraphOutputs(this.taskId, seqNos)
        .then(res => {
          const outputs = res.data || []
          if (!outputs.length) {
            return
          }
          const bySeq = {}
          outputs.forEach(item => {
            bySeq[item.seqNo] = item
          })
          this.detail.paragraphs = (this.detail.paragraphs || []).map(item => {
            const output = bySeq[item.seqNo]
            if (!output) {
              return item
            }
            return {
              ...item,
              outputText: output.outputText,
              skipped: output.skipped
            }
          })
        })
        .catch(() => {})
    },
    scrollToPayment() {
      const el = this.$el.querySelector('.payment-panel')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    goBack() {
      this.$router.push('/workbench')
    }
  }
}
</script>

<style scoped>
.task-page {
  display: flex;
  flex-direction: column;
}

.task-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 0 24px;
  margin: 0 -28px;
  padding-left: 28px;
  padding-right: 28px;
  background: linear-gradient(to bottom, rgba(25, 118, 210, 0.12) 0%, #fff 55%, #fff 100%);
  border-bottom: 1px solid #dbeafe;
}

.task-header-actions {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  font-size: 14px;
}

.task-header-main {
  flex: 1;
  min-width: 0;
}

.task-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.task-filename {
  font-size: 20px;
  color: #333;
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-badge {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid transparent;
}

.task-badge.mode {
  background: #e3f2fd;
  color: #1565c0;
  border-color: #e0e7ff;
}

.task-badge.platform {
  background: #f8fafc;
  color: #475569;
  border-color: #e2e8f0;
}

.task-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
}

.stat-value {
  color: #333;
}

.progress-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-bar {
  width: 80px;
  height: 6px;
  background: #dbeafe;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #1976d2;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.progress-text {
  color: #333;
  font-size: 12px;
}

.task-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.regenerate-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px dashed #c7c9ff;
  border-radius: 999px;
  background: #fafafe;
  color: #1976d2;
  cursor: pointer;
  font-size: 13px;
}

.regenerate-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border: none;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(135deg, #1976d2, #0288d1);
  cursor: pointer;
}

.download-btn.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.task-body {
  flex: 1;
  padding: 20px 0 24px;
}

.task-error,
.task-loading {
  padding: 24px;
  text-align: center;
  color: #666;
}

.paragraph-list {
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-left: 44px;
}

.payment-panel {
  display: flex;
  border: 3px solid #bbdefb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.3);
  background: #fff;
}

.payment-left {
  width: 320px;
  padding: 24px 20px;
  background: #f7f7fd;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.payment-loading {
  padding: 12px 0 16px;
  font-size: 13px;
  color: #666;
  text-align: center;
}

.recommend-tag {
  display: inline-block;
  padding: 2px 10px;
  margin-bottom: 8px;
  border-radius: 999px;
  background: #e3f2fd;
  color: #1976d2;
  font-size: 12px;
  font-weight: 600;
}

.payment-plan-name {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
}

.payment-quota,
.payment-need-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  text-align: center;
}

.payment-need-hint strong {
  color: #1976d2;
}

.payment-price {
  font-size: 42px;
  color: #333;
  line-height: 1;
}

.payment-price .unit {
  font-size: 16px;
}

.payment-rate {
  margin-top: 8px;
  font-size: 12px;
  color: #333;
}

.payment-qrcode {
  margin-top: 14px;
  width: 126px;
  height: 126px;
  border: 10px solid #fff;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qrcode-placeholder,
.qrcode-status {
  font-size: 12px;
  color: #999;
  text-align: center;
  padding: 0 8px;
}

.qrcode-status.error {
  color: #dc2626;
}

.qrcode-img {
  display: block;
  width: 106px;
  height: 106px;
}

.payment-product,
.payment-pay-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #666;
  text-align: center;
}

.payment-pay-hint {
  color: #333;
  font-weight: 500;
  margin-bottom: 4px;
}

.plan-btn,
.resume-btn {
  width: 100%;
  margin-top: 10px;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
}

.plan-btn {
  border: 1px solid #1976d2;
  background: #dbeafe;
  color: #1976d2;
}

.resume-btn {
  border: none;
  background: #1976d2;
  color: #fff;
}

.resume-btn.secondary {
  background: #fff;
  color: #1976d2;
  border: 1px solid #c7c9ff;
}

.payment-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
  text-align: center;
}

.payment-right {
  flex: 1;
  padding: 28px 32px;
}

.payment-right h2 {
  margin: 0 0 8px;
  font-size: 20px;
  color: #1f2937;
}

.payment-right p {
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 14px;
}

.highlight {
  color: #1976d2;
}

.payment-features {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 14px;
}

.payment-features li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.payment-features i {
  color: rgba(25, 118, 210, 0.5);
  font-size: 18px;
  margin-top: 2px;
}

.payment-features strong {
  display: block;
  font-size: 14px;
  color: #1f2937;
}

.payment-features span {
  display: block;
  font-size: 12px;
  color: #9ca3af;
}

.paragraph-card {
  position: relative;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  overflow: visible;
}

.table-card {
  position: relative;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-index {
  position: absolute;
  left: -44px;
  top: 18px;
  width: 36px;
  text-align: right;
  color: rgba(25, 118, 210, 0.35);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.table-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #dbeafe;
  background: #fafafe;
}

.table-badge {
  padding: 2px 10px;
  border-radius: 999px;
  background: #e3f2fd;
  color: #1976d2;
  font-size: 12px;
  font-weight: 600;
}

.table-scroll {
  overflow-x: auto;
}

.doc-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
}

.doc-table td {
  border: 1px solid #dbeafe;
  padding: 10px 12px;
  vertical-align: top;
  min-width: 120px;
}

.table-cell-seq {
  font-size: 11px;
  color: rgba(25, 118, 210, 0.45);
  margin-bottom: 6px;
  font-weight: 600;
}

.table-cell-label {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.table-cell-label.output {
  margin-top: 8px;
}

.table-cell-text {
  white-space: pre-wrap;
  word-break: break-word;
  color: #333;
  font-size: 13px;
  line-height: 1.6;
}

.table-cell-text.output {
  color: #1565c0;
}

.table-cell-diff,
.paragraph-diff-view {
  flex: 1;
  min-width: 0;
}

.paragraph-diff-row {
  background: #fafafe;
}

.paragraph-diff-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666;
  font-size: 12px;
  cursor: pointer;
}

.paragraph-diff-toggle:hover {
  background: #e3f2fd;
  color: #1565c0;
}

.table-cell-status {
  margin-top: 8px;
  font-size: 11px;
}

.table-cell-status .paragraph-skip-tip,
.table-cell-status .paragraph-pay-tip,
.table-cell-status .paragraph-done-tip,
.table-cell-status .paragraph-processing-tip,
.table-cell-status .paragraph-pending-tip {
  font-size: 11px;
}

.table-cell-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.table-cell-actions .paragraph-action-btn {
  padding: 4px 8px;
  font-size: 11px;
}

.paragraph-index {
  position: absolute;
  left: -44px;
  top: 18px;
  width: 36px;
  text-align: right;
  color: rgba(25, 118, 210, 0.35);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.paragraph-row {
  display: flex;
  gap: 20px;
  padding: 16px 20px;
  border: 1px solid #dbeafe;
}

.output-row {
  border-top: none;
  background: #fafafe;
}

.paragraph-label {
  width: 40px;
  flex-shrink: 0;
  color: #666;
  font-size: 14px;
}

.paragraph-text {
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: #333;
  font-size: 14px;
  line-height: 1.7;
}

.paragraph-footer {
  padding: 8px 20px;
  background: #f7f7fd;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.paragraph-footer-status {
  display: flex;
  align-items: center;
  min-width: 0;
}

.paragraph-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.paragraph-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.paragraph-action-btn.ghost {
  background: #fff;
  border-color: #d8d8ef;
  color: #475569;
}

.paragraph-action-btn.ghost:hover:not(:disabled) {
  border-color: #1976d2;
  color: #1976d2;
}

.paragraph-action-btn.primary {
  background: #1976d2;
  color: #fff;
}

.paragraph-action-btn.primary:hover:not(:disabled) {
  background: #1565c0;
}

.paragraph-action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.paragraph-pay-tip,
.paragraph-done-tip,
.paragraph-processing-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
}

.paragraph-done-tip {
  color: #059669;
}

.paragraph-skip-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #94a3b8;
}

.paragraph-processing-tip {
  color: #1976d2;
}

.paragraph-pending-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #9ca3af;
}

.payment-affix {
  position: fixed;
  right: 70px;
  bottom: 300px;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  font-size: 13px;
  color: #333;
}

.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .task-header {
    flex-direction: column;
    align-items: stretch;
  }

  .task-header-right {
    width: 100%;
  }

  .download-btn {
    width: 100%;
    justify-content: center;
  }

  .payment-panel {
    flex-direction: column;
  }

  .payment-left {
    width: 100%;
  }

  .paragraph-list {
    padding-left: 0;
  }

  .paragraph-index {
    position: static;
    width: auto;
    text-align: left;
    padding: 12px 20px 0;
    color: rgba(25, 118, 210, 0.45);
  }

  .table-index {
    position: static;
    width: auto;
    text-align: left;
    padding: 12px 20px 0;
    color: rgba(25, 118, 210, 0.45);
  }

  .payment-affix {
    right: 16px;
    bottom: 24px;
  }
}
</style>
